import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

type Step = "choose" | "phone" | "otp" | "register" | "email" | "email-register" | "forgot-password" | "forgot-sent";
type AuthMethod = "phone" | "email";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, isLoggedIn, sendOtp, verifyOtp, findOrCreateCustomer, loginOrRegisterWithEmail } = useAuth();

  const [step, setStep] = useState<Step>("choose");
  const [authMethod, setAuthMethod] = useState<AuthMethod>("phone");

  // Phone flow
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [otpAutoSent, setOtpAutoSent] = useState(false);

  // Email flow
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailIsNew, setEmailIsNew] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // Forgot password flow
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSentEmail, setForgotSentEmail] = useState("");

  // Shared
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isLoggedIn) navigate("/account");
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    setCanResend(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validatePhone = (val: string): boolean => {
    const cleaned = val.replace(/[\s\-]/g, "");
    return (
      /^05\d{8}$/.test(cleaned) ||
      /^5\d{8}$/.test(cleaned) ||
      /^\+9665\d{8}$/.test(cleaned) ||
      /^009665\d{8}$/.test(cleaned) ||
      /^9665\d{8}$/.test(cleaned)
    );
  };

  const normalizePhone = (val: string): string => {
    const cleaned = val.replace(/[\s\-]/g, "");
    if (/^05\d{8}$/.test(cleaned)) return cleaned;
    if (/^5\d{8}$/.test(cleaned)) return "0" + cleaned;
    if (/^\+9665\d{8}$/.test(cleaned)) return "0" + cleaned.slice(4);
    if (/^009665\d{8}$/.test(cleaned)) return "0" + cleaned.slice(5);
    if (/^9665\d{8}$/.test(cleaned)) return "0" + cleaned.slice(3);
    return cleaned;
  };

  const validateEmail = (val: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  };

  // ── Phone flow handlers ──
  const handleSendOtp = async () => {
    const raw = phone.replace(/[\s\-]/g, "");
    if (!validatePhone(raw)) {
      setPhoneError("يرجى إدخال رقم جوال سعودي صحيح — مثال: 0512345678");
      return;
    }
    const normalized = normalizePhone(raw);
    setPhone(normalized);
    setPhoneError("");
    setIsLoading(true);
    setLoadingMsg("جاري إرسال رمز التحقق...");

    const { error, whatsappUrl: waUrl, autoSent } = await sendOtp(normalized);
    setIsLoading(false);

    if (error) { setPhoneError(error); return; }

    setOtpAutoSent(!!autoSent);
    if (waUrl && !autoSent) {
      setWhatsappUrl(waUrl);
      window.open(waUrl, "_blank", "noopener,noreferrer");
    }

    setStep("otp");
    startCountdown();
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError("");
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const entered = otp.join("");
    if (entered.length < 6) { setOtpError("يرجى إدخال الرمز كاملاً (6 أرقام)"); return; }

    setIsLoading(true);
    setLoadingMsg("جاري التحقق من الرمز...");

    const cleaned = normalizePhone(phone);
    const { error, isNew } = await verifyOtp(cleaned, entered);

    if (error) { setIsLoading(false); setOtpError(error); return; }

    setIsNewUser(isNew);

    if (!isNew) {
      setLoadingMsg("جاري تسجيل الدخول...");
      const customer = await findOrCreateCustomer(normalizePhone(phone));
      setIsLoading(false);
      if (customer) { login(customer); navigate("/account"); }
      else setOtpError("حدث خطأ، حاول مرة أخرى");
    } else {
      setIsLoading(false);
      setStep("register");
    }
  };

  const handleRegister = async () => {
    if (!name.trim() || name.trim().length < 3) {
      setNameError("يرجى إدخال اسمك الكامل (3 أحرف على الأقل)");
      return;
    }
    setNameError("");
    setIsLoading(true);
    setLoadingMsg("جاري إنشاء حسابك...");

    const cleaned = normalizePhone(phone);
    const customer = await findOrCreateCustomer(cleaned, name.trim());
    setIsLoading(false);

    if (customer) { login(customer); navigate("/account"); }
    else setNameError("حدث خطأ أثناء إنشاء الحساب، حاول مرة أخرى");
  };

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setIsLoading(true);
    setLoadingMsg("جاري إعادة الإرسال...");
    const cleaned = normalizePhone(phone);
    const { error, whatsappUrl: waUrl, autoSent } = await sendOtp(cleaned);
    setIsLoading(false);
    if (error) {
      setOtpError(error);
    } else {
      setOtpAutoSent(!!autoSent);
      if (waUrl && !autoSent) { setWhatsappUrl(waUrl); window.open(waUrl, "_blank", "noopener,noreferrer"); }
      startCountdown();
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  };

  // ── Email flow handlers ──
  const handleEmailContinue = async () => {
    if (!validateEmail(email)) {
      setEmailError("يرجى إدخال بريد إلكتروني صحيح");
      return;
    }
    if (!password || password.length < 6) {
      setPasswordError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    setEmailError("");
    setPasswordError("");
    setIsLoading(true);
    setLoadingMsg("جاري التحقق من البريد الإلكتروني...");

    const { error, isNew, user } = await loginOrRegisterWithEmail(email.trim().toLowerCase(), password);
    setIsLoading(false);

    if (error) { setEmailError(error); return; }

    if (user) {
      // User returned (existing or newly created) — login directly
      login(user);
      navigate("/account");
      return;
    }

    if (isNew) {
      // New user — ask for name
      setEmailIsNew(true);
      setStep("email-register");
    }
  };

  const handleEmailRegister = async () => {
    if (!name.trim() || name.trim().length < 3) {
      setNameError("يرجى إدخال اسمك الكامل (3 أحرف على الأقل)");
      return;
    }
    if (!password || password.length < 6) {
      setPasswordError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    if (password !== passwordConfirm) {
      setPasswordError("كلمتا المرور غير متطابقتين");
      return;
    }
    setNameError("");
    setPasswordError("");
    setIsLoading(true);
    setLoadingMsg("جاري إنشاء حسابك...");

    const { error, user, isNew } = await loginOrRegisterWithEmail(email.trim().toLowerCase(), password, name.trim());
    setIsLoading(false);

    if (error) { setNameError(error); return; }

    if (user) {
      login(user);
      navigate("/account");
      return;
    }

    // Fallback: if user not returned but no error, try login again
    if (isNew) {
      setNameError("حدث خطأ أثناء إنشاء الحساب، حاول مرة أخرى");
    }
  };

  // ── Forgot password handler ──
  const handleForgotPassword = async () => {
    if (!validateEmail(forgotEmail)) {
      setForgotError("يرجى إدخال بريد إلكتروني صحيح");
      return;
    }
    setForgotError("");
    setIsLoading(true);
    setLoadingMsg("جاري إرسال رابط إعادة التعيين...");

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      await fetch(`${supabaseUrl}/functions/v1/send-reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
    } catch { /* always show success */ }

    setIsLoading(false);
    setForgotSentEmail(forgotEmail.trim().toLowerCase());
    setStep("forgot-sent");
  };

  const selectMethod = (method: AuthMethod) => {
    setAuthMethod(method);
    setStep(method === "phone" ? "phone" : "email");
    setPhoneError(""); setEmailError(""); setNameError("");
  };

  const goBack = () => {
    setStep("choose");
    setPhone(""); setEmail(""); setName("");
    setPassword(""); setPasswordConfirm(""); setPasswordError("");
    setOtp(["", "", "", "", "", ""]);
    setPhoneError(""); setEmailError(""); setNameError(""); setOtpError("");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-16" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-400/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img
            src="https://public.readdy.ai/ai/img_res/3f4c2435-201e-437d-afea-0ab12a1a0054.png"
            alt="الإبداع"
            className="w-16 h-16 object-contain mb-3"
          />
          <h1 className="text-white text-2xl font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
            متجر الإبداع
          </h1>
          <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {step === "choose" && "اختر طريقة تسجيل الدخول"}
            {step === "phone" && "أدخل رقم جوالك للمتابعة"}
            {step === "otp" && "تحقق من رقم جوالك"}
            {step === "register" && "أكمل إنشاء حسابك"}
            {step === "email" && "أدخل بريدك الإلكتروني"}
            {step === "email-register" && "أكمل إنشاء حسابك"}
            {step === "forgot-password" && "إعادة تعيين كلمة المرور"}
            {step === "forgot-sent" && "تحقق من بريدك الإلكتروني"}
          </p>
        </div>

        <div className="bg-[#111] border border-white/8 rounded-3xl p-8">

          {/* Loading overlay */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>{loadingMsg}</p>
            </div>
          )}

          {/* ── Step: Choose method ── */}
          {!isLoading && step === "choose" && (
            <div className="flex flex-col gap-4">
              <p className="text-gray-400 text-sm text-center mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                سجّل دخولك أو أنشئ حساباً جديداً
              </p>

              {/* Phone option */}
              <button
                onClick={() => selectMethod("phone")}
                className="w-full flex items-center gap-4 bg-[#1a1a1a] hover:bg-[#222] border border-white/8 hover:border-amber-400/30 rounded-2xl px-5 py-4 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-400/10 rounded-xl flex-shrink-0 group-hover:bg-green-400/20 transition-colors">
                  <i className="ri-whatsapp-line text-green-400 text-xl"></i>
                </div>
                <div className="text-right flex-1">
                  <p className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الجوال</p>
                  <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>تحقق عبر واتساب برمز 6 أرقام</p>
                </div>
                <i className="ri-arrow-left-line text-gray-600 group-hover:text-amber-400 transition-colors"></i>
              </button>

              {/* Email option */}
              <button
                onClick={() => selectMethod("email")}
                className="w-full flex items-center gap-4 bg-[#1a1a1a] hover:bg-[#222] border border-white/8 hover:border-amber-400/30 rounded-2xl px-5 py-4 transition-all cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0 group-hover:bg-amber-400/20 transition-colors">
                  <i className="ri-mail-line text-amber-400 text-xl"></i>
                </div>
                <div className="text-right flex-1">
                  <p className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>البريد الإلكتروني</p>
                  <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>تسجيل دخول مباشر بالبريد الإلكتروني</p>
                </div>
                <i className="ri-arrow-left-line text-gray-600 group-hover:text-amber-400 transition-colors"></i>
              </button>

              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-white/5"></div>
                <span className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>بياناتك محمية ومحفوظة بأمان</span>
                <div className="flex-1 h-px bg-white/5"></div>
              </div>

              <div className="flex items-center justify-center gap-2 text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-lock-2-line text-amber-400/60"></i>
                <span>لا حاجة لكلمة مرور — تسجيل سريع وآمن</span>
              </div>
            </div>
          )}

          {/* ── Step: Phone ── */}
          {!isLoading && step === "phone" && (
            <div className="flex flex-col gap-5">
              <button onClick={goBack} className="flex items-center gap-2 text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap w-fit" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-arrow-right-line"></i>
                تغيير طريقة التسجيل
              </button>

              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  رقم الجوال <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">
                    🇸🇦 +966
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                    placeholder="05XXXXXXXX"
                    maxLength={15}
                    className={`w-full bg-[#1a1a1a] border ${phoneError ? "border-red-500/60" : "border-white/10"} rounded-2xl pr-28 pl-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                    autoFocus
                  />
                </div>
                {phoneError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{phoneError}
                  </p>
                )}
              </div>

              <button
                onClick={handleSendOtp}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-whatsapp-line text-base"></i>
                إرسال رمز التحقق عبر واتساب
              </button>

              <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3 flex items-start gap-3">
                <i className="ri-smartphone-line text-amber-400 text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  سيُفتح واتساب تلقائياً برسالة تحتوي رمز التحقق المكوّن من 6 أرقام
                </p>
              </div>
            </div>
          )}

          {/* ── Step: OTP ── */}
          {!isLoading && step === "otp" && (
            <div className="flex flex-col gap-5">
              <div className="text-center">
                <div className={`w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-3 ${otpAutoSent ? "bg-amber-400/15" : "bg-green-400/10"}`}>
                  <i className={`text-2xl ${otpAutoSent ? "ri-message-2-line text-amber-400" : "ri-whatsapp-line text-green-400"}`}></i>
                </div>
                <p className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {otpAutoSent ? "تم إرسال رمز التحقق إلى" : "تم فتح واتساب لإرسال رمز التحقق إلى"}
                </p>
                <p className="text-white font-bold text-base mt-1 tracking-widest" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {phone}
                </p>
              </div>

              {otpAutoSent ? (
                <div className="bg-amber-400/8 border border-amber-400/25 rounded-2xl px-4 py-3 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-amber-400 text-base flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>تم إرسال الرمز من رقم المتجر عبر واتساب</p>
                    <p className="text-amber-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>تحقق من رسائل واتساب وأدخل الرمز المكوّن من 6 أرقام</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-400/8 border border-green-400/25 rounded-2xl px-4 py-3 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-whatsapp-line text-green-400 text-base flex-shrink-0"></i>
                    <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>تحقق من واتساب وأدخل الرمز أدناه</p>
                  </div>
                  {whatsappUrl && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-white font-black text-xs py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap mt-1"
                      style={{ fontFamily: "'Cairo', sans-serif" }}>
                      <i className="ri-whatsapp-line text-sm"></i>
                      فتح واتساب للحصول على الرمز
                    </a>
                  )}
                </div>
              )}

              <div className="flex items-center justify-center gap-2 mt-2" dir="ltr">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`w-12 h-14 text-center text-white text-xl font-black bg-[#1a1a1a] border ${
                      otpError ? "border-red-500/60" : digit ? "border-amber-400/50" : "border-white/10"
                    } rounded-xl outline-none focus:border-amber-400 transition-colors`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="text-red-400 text-xs text-center flex items-center justify-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-error-warning-line"></i>{otpError}
                </p>
              )}

              <button
                onClick={handleVerifyOtp}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-shield-check-line text-base"></i>
                تحقق وتابع
              </button>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => { setStep("phone"); setOtp(["", "", "", "", "", ""]); setOtpError(""); }}
                  className="text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-arrow-right-line ml-1"></i>
                  تغيير الرقم
                </button>
                {canResend ? (
                  <button onClick={handleResend} className="text-amber-400 hover:text-amber-300 text-xs font-bold transition-colors cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-refresh-line ml-1"></i>
                    إعادة الإرسال
                  </button>
                ) : (
                  <span className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>إعادة الإرسال بعد {countdown}ث</span>
                )}
              </div>
            </div>
          )}

          {/* ── Step: Register (phone) ── */}
          {!isLoading && step === "register" && (
            <div className="flex flex-col gap-5">
              <div className="text-center mb-2">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/15 rounded-full mx-auto mb-3">
                  <i className="ri-user-add-line text-amber-400 text-2xl"></i>
                </div>
                <p className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>مرحباً بك في متجر الإبداع!</p>
                <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>أدخل اسمك لإكمال إنشاء حسابك</p>
              </div>

              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  الاسم الكامل <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                  placeholder="مثال: محمد أحمد العتيبي"
                  className={`w-full bg-[#1a1a1a] border ${nameError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                  autoFocus
                />
                {nameError && <p className="text-red-400 text-xs mt-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{nameError}</p>}
              </div>

              <div className="bg-[#1a1a1a] rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-green-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الجوال المُتحقق منه</p>
                  <p className="text-white text-sm font-bold tracking-wider" style={{ fontFamily: "'Cairo', sans-serif" }}>{phone}</p>
                </div>
              </div>

              <button
                onClick={handleRegister}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-user-follow-line text-base"></i>
                إنشاء الحساب وحفظه
              </button>
            </div>
          )}

          {/* ── Step: Email ── */}
          {!isLoading && step === "email" && (
            <div className="flex flex-col gap-5">
              <button onClick={goBack} className="flex items-center gap-2 text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap w-fit" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-arrow-right-line"></i>
                تغيير طريقة التسجيل
              </button>

              <div className="text-center mb-1">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-400/10 rounded-full mx-auto mb-3">
                  <i className="ri-mail-line text-amber-400 text-2xl"></i>
                </div>
                <p className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  أدخل بريدك الإلكتروني وكلمة المرور
                </p>
              </div>

              {/* Email field */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  البريد الإلكتروني <span className="text-amber-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailContinue()}
                  placeholder="example@email.com"
                  className={`w-full bg-[#1a1a1a] border ${emailError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
                  style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}
                  autoFocus
                />
                {emailError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{emailError}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  كلمة المرور <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailContinue()}
                    placeholder="6 أحرف على الأقل"
                    className={`w-full bg-[#1a1a1a] border ${passwordError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 pl-12`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{passwordError}
                  </p>
                )}
              </div>

              <button
                onClick={handleEmailContinue}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-arrow-left-line text-base"></i>
                متابعة
              </button>

              {/* Forgot password link */}
              <div className="flex items-center justify-between">
                <div className="bg-white/3 border border-white/8 rounded-xl px-3 py-2.5 flex items-center gap-2 flex-1">
                  <i className="ri-information-line text-amber-400/70 text-sm flex-shrink-0"></i>
                  <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    بريد جديد؟ سننشئ لك حساباً تلقائياً
                  </p>
                </div>
                <button
                  onClick={() => { setForgotEmail(email); setForgotError(""); setStep("forgot-password"); }}
                  className="text-amber-400 hover:text-amber-300 text-xs font-bold transition-colors cursor-pointer whitespace-nowrap mr-3 flex-shrink-0"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-lock-password-line ml-1"></i>
                  نسيت كلمة المرور؟
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Forgot Password ── */}
          {!isLoading && step === "forgot-password" && (
            <div className="flex flex-col gap-5">
              <button
                onClick={() => { setStep("email"); setForgotError(""); }}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap w-fit"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-arrow-right-line"></i>
                العودة لتسجيل الدخول
              </button>

              <div className="text-center mb-1">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-full mx-auto mb-3">
                  <i className="ri-lock-password-line text-amber-400 text-2xl"></i>
                </div>
                <p className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>نسيت كلمة المرور؟</p>
                <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  أدخل بريدك وسنرسل لك رابط إعادة التعيين
                </p>
              </div>

              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  البريد الإلكتروني <span className="text-amber-400">*</span>
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
                  placeholder="example@email.com"
                  className={`w-full bg-[#1a1a1a] border ${forgotError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
                  style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}
                  autoFocus
                />
                {forgotError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{forgotError}
                  </p>
                )}
              </div>

              <button
                onClick={handleForgotPassword}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-send-plane-line text-base"></i>
                إرسال رابط إعادة التعيين
              </button>

              <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3 flex items-start gap-3">
                <i className="ri-time-line text-amber-400 text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  الرابط صالح لمدة 30 دقيقة فقط — تحقق من بريدك الوارد أو مجلد الـ Spam
                </p>
              </div>
            </div>
          )}

          {/* ── Step: Forgot Password Sent ── */}
          {!isLoading && step === "forgot-sent" && (
            <div className="flex flex-col gap-5">
              <div className="text-center py-4">
                <div className="w-20 h-20 flex items-center justify-center bg-green-400/10 rounded-full mx-auto mb-4">
                  <i className="ri-mail-send-line text-green-400 text-4xl"></i>
                </div>
                <p className="text-white font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif" }}>تم الإرسال!</p>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  أرسلنا رابط إعادة تعيين كلمة المرور إلى:
                </p>
                <p className="text-amber-400 font-bold text-sm mt-1" style={{ direction: "ltr" }}>{forgotSentEmail}</p>
              </div>

              <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl px-5 py-4 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-full flex-shrink-0">
                    <i className="ri-mail-open-line text-amber-400 text-sm"></i>
                  </div>
                  <p className="text-gray-300 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>افتح بريدك الإلكتروني</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-full flex-shrink-0">
                    <i className="ri-cursor-line text-amber-400 text-sm"></i>
                  </div>
                  <p className="text-gray-300 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>اضغط على زر &quot;إعادة تعيين كلمة المرور&quot;</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-full flex-shrink-0">
                    <i className="ri-lock-password-line text-amber-400 text-sm"></i>
                  </div>
                  <p className="text-gray-300 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>أدخل كلمة مرورك الجديدة</p>
                </div>
              </div>

              <div className="bg-amber-400/8 border border-amber-400/20 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="ri-spam-2-line text-amber-400 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-amber-400/80 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  لم تجد الرسالة؟ تحقق من مجلد الـ Spam أو البريد غير المرغوب
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setStep("forgot-password"); setForgotError(""); }}
                  className="w-full bg-[#1a1a1a] hover:bg-[#222] border border-white/8 text-gray-300 font-bold py-3 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-refresh-line text-base"></i>
                  إعادة الإرسال
                </button>
                <button
                  onClick={() => setStep("email")}
                  className="text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap text-center"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-arrow-right-line ml-1"></i>
                  العودة لتسجيل الدخول
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Email Register ── */}
          {!isLoading && step === "email-register" && (
            <div className="flex flex-col gap-5">
              <div className="text-center mb-2">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/15 rounded-full mx-auto mb-3">
                  <i className="ri-user-add-line text-amber-400 text-2xl"></i>
                </div>
                <p className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>مرحباً بك في متجر الإبداع!</p>
                <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>أكمل بياناتك لإنشاء حسابك</p>
              </div>

              {/* Name field */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  الاسم الكامل <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailRegister()}
                  placeholder="مثال: محمد أحمد العتيبي"
                  className={`w-full bg-[#1a1a1a] border ${nameError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                  autoFocus
                />
                {nameError && <p className="text-red-400 text-xs mt-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{nameError}</p>}
              </div>

              {/* Password confirm field */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  تأكيد كلمة المرور <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswordConfirm ? "text" : "password"}
                    value={passwordConfirm}
                    onChange={(e) => { setPasswordConfirm(e.target.value); setPasswordError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailRegister()}
                    placeholder="أعد إدخال كلمة المرور"
                    className={`w-full bg-[#1a1a1a] border ${passwordError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 pl-12`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showPasswordConfirm ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{passwordError}
                  </p>
                )}
              </div>

              <div className="bg-[#1a1a1a] rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full flex-shrink-0">
                  <i className="ri-mail-check-line text-amber-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>البريد الإلكتروني</p>
                  <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}>{email}</p>
                </div>
              </div>

              <button
                onClick={handleEmailRegister}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-user-follow-line text-base"></i>
                إنشاء الحساب وحفظه
              </button>

              <button
                onClick={() => { setStep("email"); setName(""); setNameError(""); setPasswordConfirm(""); setPasswordError(""); }}
                className="text-gray-500 hover:text-gray-300 text-xs transition-colors cursor-pointer whitespace-nowrap text-center"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-arrow-right-line ml-1"></i>
                تغيير البريد الإلكتروني
              </button>
            </div>
          )}
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <a href="/" className="text-gray-600 hover:text-gray-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-arrow-right-line ml-1"></i>
            العودة للمتجر
          </a>
        </div>
      </div>
    </div>
  );
}
